import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { SAvatar } from '@/components/ui/atoms/SAvatar';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import memberService from '@/services/member.service';
import { MembershipStatusBanner } from '@/components/ui/organisms/MembershipStatusBanner';
import { PastDueGate } from '@/components/ui/organisms/PastDueGate';
import SLoader from '@/components/ui/atoms/SLoader';
import { useMemberAccess } from '@/hooks/useMemberAccess';
import { useLiveMemberStatus } from '@/hooks/useLiveMemberStatus';
import './HomeLayout.css';
import { currentUiVersion } from '@/v2/lib/uiVersion';
import { UiBlock, V2PastDueGate } from '@/v2/routes';

/**
 * HomeLayout: Used for authenticated home/dashboard screens
 * Includes header with logo and action icons
 */
const HomeLayout: React.FC = () => {
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const { isPastDue, isChecking } = useMemberAccess();
  useLiveMemberStatus();

  // Fetch avatar URL on mount
  useEffect(() => {
    const fetchAvatarUrl = async () => {
      try {
        const response = await memberService.getAvatarUrl();
        setAvatarUrl(response.url);
      } catch (error: any) {
        // If member doesn't have an avatar (400 error), keep avatarUrl empty to show initials
        if (error?.response?.status === 400) {
          setAvatarUrl('');
        } else {
          console.error('Failed to fetch avatar URL:', error);
        }
      }
    };
    
    fetchAvatarUrl();
  }, []);

  /* The v2 home screen is a full-viewport shell: it brings its own
     header (logo + greeting over the hero) and its own bottom tab
     bar. Nesting it inside this layout's header and scroll
     container double-stacks the header and pushes the tab bar off
     the bottom of the screen.

     THE PAST-DUE BLOCK HAS TO BE REPEATED HERE. This return sits
     above the `isPastDue` branch below, so a v2 session used to
     skip the gate entirely and land on a full dashboard while the
     membership was past_due. The v1 gate cannot simply be reused —
     it is styled for v1's canvas — so the v2 tree carries its own,
     lazy-loaded through `UiBlock`. */
  if (currentUiVersion() === 'v2') {
    if (isChecking) {
      /* NO SPINNER HERE ON v2. The screen underneath renders its own
         skeleton the moment it mounts, so v1's logo loader made the
         member watch two different loading states in a row — a
         branded animation, then a shimmer. A plain branded surface
         holds the frame for the access check and hands straight over
         to the skeleton. */
      return (
        <div style={{ minHeight: '100dvh', background: 'var(--tb-bg, #FFFFFF)' }} />
      );
    }
    if (isPastDue) {
      return (
        <UiBlock
          v1={<PastDueGate />}
          v2={<V2PastDueGate />}
          /* Page-sized, so it carries a branded surface rather than
             flashing white while the chunk resolves. */
          fallback={<div style={{ minHeight: '100dvh', background: 'var(--tb-bg, #FFFFFF)' }} />}
        />
      );
    }
    /* The banner is fixed-position on v2 (the shell below it is a
       full-viewport `.tb-app`, so an in-flow strip would push the
       tab bar off the bottom), which is why it sits beside the
       Outlet rather than above it. */
    return (
      <>
        <MembershipStatusBanner />
        <Outlet />
      </>
    );
  }

  return (
    <div className="home-layout">
      <div className="home-layout-header">
        <div className="home-header-content">
          <div className="home-logo">
            <img className="logo-image" src="/assets/teebox-header.png" alt="TEE BOX" />
          </div>
          <div className="home-header-icons">
            <ThemeToggle />
            <button className="icon-button" onClick={() => navigate('/profile')}>
              <SAvatar variant="sm" avatarUrl={avatarUrl} />
            </button>
          </div>
        </div>
      </div>
      {/* past_due renders its own full-screen message — no duplicate banner */}
      {!isPastDue && <MembershipStatusBanner />}
      <main className="home-layout-content">
        {isChecking ? (
          <div className="home-layout-checking">
            <SLoader />
          </div>
        ) : isPastDue ? (
          <PastDueGate />
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
};

export default HomeLayout;
